/**
 * CvRenderer.jsx — RC 1.41.G shared structured/markdown CV renderer.
 *
 * One component, three modes:
 *
 *   <CvRenderer mode="source"  data={baseCvRow}  showDisclaimer />
 *   <CvRenderer mode="variant" data={variantRow} />
 *   <CvRenderer mode="letter"  data={letterRow} />
 *
 * Resolution order (per mode)
 * ---------------------------
 *  source  : data.parsed_json.structured (schema v1)
 *           → data.parsed_text  (markdown subset render)
 *           → <pre whitespace-pre-wrap> fallback.
 *  variant : data.content_markdown (markdown subset render)
 *           → <pre> fallback.
 *  letter  : same as variant.
 *
 * The renderer never throws. Any malformed input collapses to the
 * <pre> fallback so existing screens render exactly as they did
 * pre-RC 1.41.G.
 *
 * Hard rails (RC 1.41.G):
 *   * No new HTTP calls.
 *   * No mutations.
 *   * Disclaimer only on `mode='source'` AND when the structured
 *     path was used.
 *   * Mobile-first: A4-ratio aware on >= sm, single-column on <sm.
 */

import { Mail, Phone, Link as LinkIcon, MapPin } from 'lucide-react';

const DISCLAIMER =
  'Uploaded formatting may be normalized for optimal analysis and generation.';

// ─── Tiny markdown subset renderer ────────────────────────────────
//
// Supports headings (#, ##, ###), unordered lists ("- " or "* "),
// horizontal rule (---), bold (**…**), italic (*…* / _…_),
// code-spans (`…`). Blank lines separate paragraphs. No raw HTML.
// LLM output for variants + cover letters is well-formed markdown,
// and CV flat text rarely contains markdown — both render
// acceptably.

function renderInline(text) {
  // Tokenise on **bold**, _italic_, *italic*, `code`. Order matters:
  // bold first so `**text**` doesn't get parsed as nested italics.
  const out = [];
  let cursor = 0;
  const re = /(\*\*[^*]+\*\*)|(`[^`]+`)|(\*[^*\n]+\*|_[^_\n]+_)/g;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > cursor) out.push(text.slice(cursor, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) {
      out.push(<strong key={`b-${key++}`}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('`')) {
      out.push(<code key={`c-${key++}`} className="font-mono-ui text-[0.95em] bg-ndor-surface-alt px-1 rounded">{tok.slice(1, -1)}</code>);
    } else {
      out.push(<em key={`i-${key++}`}>{tok.slice(1, -1)}</em>);
    }
    cursor = m.index + tok.length;
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}

function MarkdownSubset({ text, className = '' }) {
  if (!text || typeof text !== 'string') return null;
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let cur = null; // {type:'p'|'ul', items:[]}

  const flush = () => {
    if (cur) blocks.push(cur);
    cur = null;
  };

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const ln = raw.trimEnd();

    if (ln.trim() === '') { flush(); continue; }

    // HR
    if (/^---+$/.test(ln.trim())) { flush(); blocks.push({ type: 'hr' }); continue; }

    // Heading
    const h = /^(#{1,4})\s+(.+)$/.exec(ln.trim());
    if (h) { flush(); blocks.push({ type: 'h', level: h[1].length, text: h[2] }); continue; }

    // List item
    const li = /^\s*[-*\u2022]\s+(.+)$/.exec(raw);
    if (li) {
      if (!cur || cur.type !== 'ul') { flush(); cur = { type: 'ul', items: [] }; }
      cur.items.push(li[1]);
      continue;
    }

    // Paragraph
    if (!cur || cur.type !== 'p') { flush(); cur = { type: 'p', text: [] }; }
    cur.text.push(ln.trim());
  }
  flush();

  return (
    <div className={`space-y-3 ${className}`}>
      {blocks.map((b, i) => {
        if (b.type === 'hr') return <hr key={i} className="border-ndor-edge" />;
        if (b.type === 'h') {
          const Tag = `h${Math.min(b.level + 1, 4)}`;
          const cls = b.level === 1
            ? 'text-lg font-serif-display text-ndor-ink mt-2 mb-1'
            : b.level === 2
              ? 'text-base font-medium text-ndor-ink mt-2 mb-1 uppercase tracking-[0.12em]'
              : 'text-sm font-medium text-ndor-ink mt-1 mb-0.5 uppercase tracking-[0.12em]';
          return <Tag key={i} className={cls}>{renderInline(b.text)}</Tag>;
        }
        if (b.type === 'ul') {
          return (
            <ul key={i} className="list-disc pl-5 space-y-1 text-sm text-ndor-ink leading-relaxed">
              {b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
            </ul>
          );
        }
        return (
          <p key={i} className="text-sm text-ndor-ink leading-relaxed">
            {renderInline(b.text.join(' '))}
          </p>
        );
      })}
    </div>
  );
}


// ─── Structured renderer (schema v1) ──────────────────────────────

function ContactItem({ kind, value }) {
  const Icon = kind === 'email' ? Mail
    : kind === 'phone' ? Phone
    : kind === 'location' ? MapPin
    : LinkIcon;
  const isLink = kind === 'email' || kind === 'link' || kind === 'url';
  const href = kind === 'email' ? `mailto:${value}` : (isLink ? value : null);
  const inner = (
    <span className="inline-flex items-center gap-1.5 text-xs text-ndor-ink-secondary">
      <Icon className="h-3 w-3 text-ndor-ink-muted shrink-0" aria-hidden />
      <span className="break-all">{value}</span>
    </span>
  );
  return href
    ? <a href={href} target="_blank" rel="noopener noreferrer" className="hover:text-ndor-ink underline-offset-2 hover:underline">{inner}</a>
    : inner;
}

function Block({ block }) {
  if (!block || typeof block !== 'object') return null;
  switch (block.type) {
    case 'paragraph':
      return <p className="text-sm text-ndor-ink leading-relaxed">{block.text}</p>;
    case 'list':
      return (
        <ul className={`${block.ordered ? 'list-decimal' : 'list-disc'} pl-5 space-y-1 text-sm text-ndor-ink leading-relaxed`}>
          {(block.items || []).map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      );
    case 'experience_entry':
      return (
        <div className="space-y-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            {block.role && <span className="text-sm font-medium text-ndor-ink">{block.role}</span>}
            {block.company && <span className="text-sm text-ndor-ink-secondary">— {block.company}</span>}
            {block.location && <span className="text-xs text-ndor-ink-muted">· {block.location}</span>}
          </div>
          {(block.start || block.end) && (
            <p className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted font-mono-ui">
              {block.start || '?'} {block.end ? `— ${block.end}` : ''}
            </p>
          )}
          {block.bullets?.length > 0 && (
            <ul className="list-disc pl-5 space-y-0.5 text-sm text-ndor-ink leading-relaxed">
              {block.bullets.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          )}
        </div>
      );
    case 'table':
      return (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm border-separate border-spacing-0">
            <tbody>
              {(block.rows || []).map((row, i) => (
                <tr key={i}>
                  {(row || []).map((cell, j) => (
                    <td
                      key={j}
                      className={`px-2 py-1 align-top border border-ndor-edge ${i === 0 ? 'bg-ndor-surface-alt font-medium' : ''}`}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
}

function StructuredCv({ structured }) {
  const { header, sections } = structured;
  return (
    <div className="space-y-5" data-testid="cv-renderer-structured">
      {header && (header.name || header.title || (header.contact || []).length) && (
        <header className="space-y-1.5 pb-3 border-b border-ndor-edge" data-testid="cv-renderer-header">
          {header.name && <h2 className="text-xl sm:text-2xl font-serif-display text-ndor-ink leading-tight">{header.name}</h2>}
          {header.title && <p className="text-sm text-ndor-ink-secondary">{header.title}</p>}
          {(header.contact || []).length > 0 && (
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
              {(header.contact || []).map((c, i) => <ContactItem key={i} kind={c.kind} value={c.value} />)}
            </div>
          )}
        </header>
      )}
      {(sections || []).map((s, i) => (
        <section key={i} data-testid={`cv-renderer-section-${s.kind}`} className="space-y-2">
          <h3 className="text-[11px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui">
            {s.heading}
          </h3>
          <div className="space-y-3">
            {(s.blocks || []).map((b, j) => <Block key={j} block={b} />)}
          </div>
        </section>
      ))}
    </div>
  );
}


// ─── Public component ─────────────────────────────────────────────

export default function CvRenderer({
  mode = 'source',          // 'source' | 'variant' | 'letter'
  data,                      // base_cv row or document row
  showDisclaimer = false,
  className = '',
}) {
  if (!data || typeof data !== 'object') {
    return <p className="text-sm text-ndor-ink-muted italic">No content to display.</p>;
  }

  // Decide which path to render.
  let content = null;
  let pathUsed = 'fallback'; // 'structured' | 'markdown' | 'fallback'

  if (mode === 'source') {
    const structured = data.parsed_json?.structured;
    if (structured && Array.isArray(structured.sections) && structured.sections.length) {
      content = <StructuredCv structured={structured} />;
      pathUsed = 'structured';
    } else if (typeof data.parsed_text === 'string' && data.parsed_text.trim()) {
      content = <MarkdownSubset text={data.parsed_text} />;
      pathUsed = 'markdown';
    }
  } else {
    // variant or letter
    const md = data.content_markdown ?? data.generated_text ?? '';
    if (typeof md === 'string' && md.trim()) {
      content = <MarkdownSubset text={md} />;
      pathUsed = 'markdown';
    }
  }

  // Final fallback
  if (!content) {
    const flat =
      mode === 'source'
        ? (data.parsed_text ?? '')
        : (data.content_markdown ?? data.generated_text ?? '');
    content = (
      <pre
        className="whitespace-pre-wrap text-sm text-ndor-ink leading-relaxed font-manrope"
        data-testid="cv-renderer-fallback"
      >
        {flat || 'No content available.'}
      </pre>
    );
  }

  const showDisc = showDisclaimer && mode === 'source' && pathUsed === 'structured';

  return (
    <div
      className={`cv-renderer ${className}`}
      data-testid="cv-renderer"
      data-mode={mode}
      data-path={pathUsed}
    >
      {content}
      {showDisc && (
        <p
          className="mt-4 pt-3 border-t border-ndor-edge text-[11px] text-ndor-ink-muted italic leading-relaxed"
          data-testid="cv-renderer-disclaimer"
        >
          {DISCLAIMER}
        </p>
      )}
    </div>
  );
}
